import React, { useCallback, useEffect, useRef, useState } from "react"
import Hls from "hls.js"
import { apiUrl } from "../../api"
import "./reolinkstream.css"

const REOLINK_UID = "9527000J1PK51G4Q"
const API_BASE = "/controller/reolink"
const CAMERA_DISABLED_IMAGE = "img/casa-giu-camera-disabled.jpg"

function ReolinkStream({ cameraId = "" }) {
     const containerRef = useRef(null)
     const videoRef = useRef(null)
     const hlsRef = useRef(null)
     const hasStartedStream = useRef(false)
     const restartInProgress = useRef(false)
     const recoverInProgress = useRef(false)
     const lastProgress = useRef({ currentTime: 0, misses: 0 })
     const radarVisible = useRef(false)
     const viewerId = useRef(createViewerId())
     const [camera, setCamera] = useState({
          uid: REOLINK_UID,
          configured: false,
          cameraEnabled: false,
          ffmpegAvailable: false,
          hlsUrl: "",
          stream: "main",
     })
     const [status, setStatus] = useState("waiting")
     const [frozenFrame, setFrozenFrame] = useState("")
     const streamApiBase = cameraId ? `${API_BASE}/cameras/${cameraId}` : API_BASE

     const startStream = useCallback(async (forceRestart = false) => {
          if (restartInProgress.current) {
               return
          }

          restartInProgress.current = true
          if (forceRestart) {
               setFrozenFrame((current) => current || captureFrame(videoRef.current))
               setStatus("reconnecting")
               hlsRef.current?.destroy()
               hlsRef.current = null
               await fetch(apiUrl(`${streamApiBase}/stop?force=true&viewerId=${encodeURIComponent(viewerId.current)}`), { method: "POST" }).catch(() => {})
               await delay(750)
          }

          try {
               const config = await fetch(apiUrl(`${streamApiBase}/config`)).then((response) => response.json())
               setCamera((current) => ({ ...current, ...config }))

               if (config.cameraEnabled === false) {
                    hasStartedStream.current = false
                    setFrozenFrame("")
                    setStatus("camera-disabled")
                    return
               }

               if (!config.configured || !config.ffmpegAvailable) {
                    hasStartedStream.current = false
                    setStatus(config.ffmpegAvailable ? "needs-credentials" : "needs-ffmpeg")
                    return
               }

               hasStartedStream.current = true
               const startStatus = await fetch(apiUrl(`${streamApiBase}/start`), {
                    method: "POST",
                    headers: {
                         "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ viewerId: viewerId.current }),
               }).then((response) => response.json())

               const startHlsUrl = normalizeHlsUrl(startStatus.hlsUrl)
               if (startStatus.started && startHlsUrl) {
                    await waitForHlsPlaylist(startHlsUrl)
                    setCamera((current) => ({ ...current, ...startStatus, hlsUrl: addCacheBuster(startHlsUrl) }))
                    setStatus("streaming")
               } else {
                    hasStartedStream.current = false
                    setStatus(startStatus.reason || "not-ready")
               }
          } catch (error) {
               hasStartedStream.current = false
               setStatus(forceRestart ? "reconnecting" : "backend-offline")
          } finally {
               restartInProgress.current = false
          }
     }, [streamApiBase])

     const recoverStream = useCallback(async () => {
          if (recoverInProgress.current) {
               return
          }

          recoverInProgress.current = true
          setFrozenFrame((current) => current || captureFrame(videoRef.current))
          setStatus("reconnecting")
          try {
               const config = await fetch(apiUrl(`${streamApiBase}/config`), { cache: "no-store" }).then((response) => response.json())
               if (config.cameraEnabled === false) {
                    hlsRef.current?.destroy()
                    hlsRef.current = null
                    hasStartedStream.current = false
                    setCamera((current) => ({ ...current, ...config, hlsUrl: "" }))
                    setFrozenFrame("")
                    setStatus("camera-disabled")
                    return
               }
               const hlsUrl = normalizeHlsUrl(config.hlsUrl)
               if (config.streamRunning && config.hlsFresh && hlsUrl) {
                    await waitForHlsPlaylist(hlsUrl)
                    hlsRef.current?.destroy()
                    hlsRef.current = null
                    lastProgress.current = { currentTime: 0, misses: 0 }
                    setCamera((current) => ({ ...current, ...config, hlsUrl: addCacheBuster(hlsUrl) }))
                    setStatus("streaming")
                    return
               }

               await startStream(true)
          } catch (error) {
               await startStream(true)
          } finally {
               recoverInProgress.current = false
          }
     }, [startStream, streamApiBase])

     useEffect(() => {
          if (hasStartedStream.current) {
               return
          }

          startStream(false)
     }, [startStream])

     useEffect(() => {
          const handleRadarVisibility = (event) => {
               radarVisible.current = Boolean(event.detail?.visible)
               const video = videoRef.current
               if (!video) {
                    return
               }

               video.play().catch(() => {})
               lastProgress.current = {
                    currentTime: video.currentTime || 0,
                    misses: 0,
               }
          }

          window.addEventListener("casa-giu-radar-visibility", handleRadarVisibility)
          return () => window.removeEventListener("casa-giu-radar-visibility", handleRadarVisibility)
     }, [])

     useEffect(() => {
          if (!camera.hlsUrl || !videoRef.current) {
               return undefined
          }

          hlsRef.current?.destroy()
          hlsRef.current = null
          lastProgress.current = { currentTime: 0, misses: 0 }

          if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
               videoRef.current.src = camera.hlsUrl
               videoRef.current.muted = true
               videoRef.current.autoplay = true
               videoRef.current.play().catch(() => {})
          }
          else if (Hls.isSupported()) {
               const hls = new Hls({
                    lowLatencyMode: true,
                    liveSyncDurationCount: 2,
                    liveMaxLatencyDurationCount: 5,
               })
               hlsRef.current = hls
               hls.loadSource(camera.hlsUrl)
               hls.attachMedia(videoRef.current)
               hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                    videoRef.current.muted = true
                    videoRef.current.autoplay = true
                    videoRef.current.play().catch(() => {})
               })
               hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    videoRef.current.play().catch(() => {})
               })
               hls.on(Hls.Events.LEVEL_LOADED, () => {
                    videoRef.current.play().catch(() => {})
               })
               hls.on(Hls.Events.ERROR, (event, data) => {
                    if (data.fatal) {
                         recoverStream()
                    }
               })
          } else {
               setStatus("hls-not-supported")
               return undefined
          }

          const keepPlaying = window.setInterval(() => {
               const video = videoRef.current
               if (!video) {
                    return
               }
               if (video.paused) {
                    video.play().catch(() => {})
               }
               if (video.error) {
                    recoverStream()
               }
          }, 1000)

          const watchdog = window.setInterval(() => {
               const video = videoRef.current
               if (!video || status !== "streaming") {
                    return
               }

               if (radarVisible.current) {
                    video.play().catch(() => {})
                    lastProgress.current = {
                         currentTime: video.currentTime || lastProgress.current.currentTime,
                         misses: 0,
                    }
                    return
               }

               const previous = lastProgress.current
               const currentTime = video.currentTime || 0
               if (currentTime > previous.currentTime + 0.5) {
                    lastProgress.current = { currentTime, misses: 0 }
                    setFrozenFrame("")
                    return
               }

               const misses = previous.misses + 1
               lastProgress.current = { currentTime, misses }
               if (misses >= 2) {
                    recoverStream()
               }
          }, 12000)

          return () => {
               window.clearInterval(keepPlaying)
               window.clearInterval(watchdog)
               hlsRef.current?.destroy()
               hlsRef.current = null
          }
     }, [camera.hlsUrl, recoverStream, status])

     useEffect(() => {
          const stopStream = () => {
               const stopUrl = apiUrl(`${streamApiBase}/stop?viewerId=${encodeURIComponent(viewerId.current)}`)
               if (navigator.sendBeacon) {
                    navigator.sendBeacon(stopUrl)
                    return
               }
               fetch(stopUrl, { method: "POST", keepalive: true }).catch(() => {})
          }

          window.addEventListener("pagehide", stopStream)
          return () => window.removeEventListener("pagehide", stopStream)
     }, [camera.hlsUrl, streamApiBase])

     return (
          <div className="streamplaceholder" ref={containerRef} data-camera-uid={camera.uid || REOLINK_UID}>
               {frozenFrame && <div className="streamfreeze" style={{ backgroundImage: `url(${frozenFrame})` }} aria-hidden="true"></div>}
               {camera.hlsUrl && <video ref={videoRef} className="reolinkstream" autoPlay muted playsInline controls />}
               {!camera.hlsUrl && (
                    <img
                         className="reolinkfallbackimage"
                         src={CAMERA_DISABLED_IMAGE}
                         alt="Casa Giu Strand"
                    />
               )}
          </div>
     )
}

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds))

const createViewerId = () => {
     if (window.crypto?.randomUUID) {
          return window.crypto.randomUUID()
     }
     return `viewer-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const addCacheBuster = (hlsUrl) => {
     const separator = hlsUrl.includes("?") ? "&" : "?"
     return `${hlsUrl}${separator}v=${Date.now()}`
}

const captureFrame = (video) => {
     if (!video || !video.videoWidth || !video.videoHeight) {
          return ""
     }

     try {
          const canvas = document.createElement("canvas")
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const context = canvas.getContext("2d")
          context.drawImage(video, 0, 0, canvas.width, canvas.height)
          return canvas.toDataURL("image/jpeg", 0.82)
     } catch (error) {
          return ""
     }
}

const normalizeHlsUrl = (hlsUrl) => {
     if (!hlsUrl) {
          return ""
     }

     try {
          const parsedUrl = new URL(hlsUrl, window.location.href)
          return apiUrl(`${parsedUrl.pathname}${parsedUrl.search || ""}`)
     } catch (error) {
          return apiUrl("/controller/reolink/hls/stream.m3u8")
     }
}

const waitForHlsPlaylist = async (hlsUrl) => {
     for (let attempt = 0; attempt < 100; attempt += 1) {
          try {
               const response = await fetch(hlsUrl, { cache: "no-store" })
               if (response.ok) {
                    const lastModified = response.headers.get("last-modified")
                    if (!lastModified || Date.now() - new Date(lastModified).getTime() < 10000) {
                         return
                    }
               }
          } catch (error) {
               // The backend may still be writing the first segment.
          }
          await new Promise((resolve) => setTimeout(resolve, 1000))
     }
     throw new Error("HLS playlist was not reachable")
}

export default ReolinkStream
